// src/taskpane/components/ManualTranslate.tsx
import * as React from "react";
import {
  Button,
  makeStyles,
  tokens,
  Text,
  Divider,
  Spinner,
  MessageBar,
  MessageBarBody,
} from "@fluentui/react-components";
import { ArrowSync24Regular, Copy24Regular } from "@fluentui/react-icons";
import { AddinSettings } from "../services/types";
import { translateBatch } from "../services/translator";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    fontSize: "13px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground2,
  },
  textarea: {
    width: "100%",
    minHeight: "80px",
    padding: "8px 10px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: "13px",
    lineHeight: "1.5",
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box",
    outline: "none",
    transition: "border-color 0.15s ease",
  },
  textareaFocus: {
    borderColor: tokens.colorBrandStroke1,
  },
  resultBox: {
    width: "100%",
    minHeight: "80px",
    padding: "8px 10px",
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: "13px",
    lineHeight: "1.5",
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    fontFamily: "inherit",
    boxSizing: "border-box",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  actions: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "center",
  },
});

interface ManualTranslateProps {
  sourceLang: string;
  targetLang: string;
  settings: AddinSettings;
  onInsert: (translatedText: string) => void;
}

export default function ManualTranslate({
  sourceLang,
  targetLang,
  settings,
  onInsert,
}: ManualTranslateProps) {
  const styles = useStyles();
  const [inputText, setInputText] = React.useState("");
  const [resultText, setResultText] = React.useState("");
  const [isTranslating, setIsTranslating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  const hasResult = resultText.trim().length > 0;

  /** 当输入框为空且获得焦点时，自动提取选中文本 */
  const handleFocus = React.useCallback(async () => {
    if (inputText.trim()) return;
    try {
      await PowerPoint.run(async (context) => {
        const selected = context.presentation.getSelectedShapes();
        selected.load("items");
        await context.sync();

        if (selected.items.length === 0) return;

        const shape = selected.items[0];
        const textFrame = shape.getTextFrameOrNullObject();
        textFrame.load("isNullObject, hasText, textRange/text");
        await context.sync();

        if (!textFrame.isNullObject && textFrame.hasText) {
          const text = textFrame.textRange.text;
          if (text.trim()) {
            setInputText(text);
          }
        }
      });
    } catch {
      // 忽略，用户可手动输入
    }
  }, [inputText]);

  /** 翻译 */
  const handleTranslate = async () => {
    if (!inputText.trim()) return;
    if (!settings.apiKey.trim()) {
      setError("请先在设置中配置 Azure Translator API Key");
      return;
    }

    setIsTranslating(true);
    setError(null);
    setResultText("");

    try {
      const results = await translateBatch(
        [inputText],
        targetLang,
        sourceLang === "auto" ? null : sourceLang,
        settings.apiKey,
        settings.apiRegion
      );
      setResultText(results[0]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsTranslating(false);
    }
  };

  /** 复制结果 */
  const handleCopy = () => {
    navigator.clipboard.writeText(resultText);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  /** 插入到选中的文本框 */
  const handleInsert = () => {
    if (!resultText.trim()) return;
    onInsert(resultText);
  };

  return (
    <div className={styles.root}>
      <Divider>手动翻译</Divider>

      {/* 输入区 */}
      <div className={styles.fieldGroup}>
        <div className={styles.label}>
          <span>输入文本</span>
          <Text size={100} style={{ color: tokens.colorNeutralForeground3, fontWeight: 400 }}>
            聚焦时自动读取选中文本框
          </Text>
        </div>
        <textarea
          className={styles.textarea}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onFocus={handleFocus}
          placeholder="输入需要翻译的文本，或先在幻灯片中选中文本框..."
        />
      </div>

      {/* 翻译按钮 */}
      <div className={styles.actions}>
        <Button
          appearance="primary"
          size="small"
          icon={<ArrowSync24Regular />}
          onClick={handleTranslate}
          disabled={!inputText.trim() || isTranslating}
        >
          {isTranslating ? "翻译中..." : "翻译"}
        </Button>
      </div>

      {/* 错误提示 */}
      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {/* 结果区 */}
      <div className={styles.fieldGroup}>
        <div className={styles.label}>
          <span>翻译结果</span>
        </div>
        <div className={styles.resultBox}>
          {resultText || (
            <span style={{ color: tokens.colorNeutralForeground3 }}>
              翻译结果将显示在这里
            </span>
          )}
        </div>
      </div>

      {/* 操作按钮 */}
      <div className={styles.actions}>
        <Button
          appearance="primary"
          size="small"
          onClick={handleInsert}
          disabled={!hasResult}
        >
          插入到选中文本框
        </Button>
        <Button
          appearance="subtle"
          size="small"
          icon={<Copy24Regular />}
          onClick={handleCopy}
          disabled={!hasResult}
        >
          {copied ? "已复制" : "复制"}
        </Button>
      </div>
    </div>
  );
}
